/**
 * ComplianceTable + StatusCards — lignes d'échéances (retard / 7 jours / à venir / fait) avec coche « fait ».
 */
import { CheckCircle2, RotateCcw } from "lucide-react";
import { Empty, money } from "../comptabilite/_shared";

export const STATUS = {
  overdue: { label: "En retard", cls: "bg-rose-100 text-rose-800 border-rose-200", card: "border-rose-200 bg-rose-50 text-rose-800" },
  soon: { label: "≤ 7 jours", cls: "bg-amber-100 text-amber-800 border-amber-200", card: "border-amber-200 bg-amber-50 text-amber-800" },
  upcoming: { label: "À venir", cls: "bg-slate-100 text-slate-700 border-slate-200", card: "border-slate-200 bg-white text-slate-700" },
  done: { label: "Fait", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", card: "border-emerald-200 bg-emerald-50 text-emerald-800" },
};
const CAT = { impot: "Impôt", taxes: "TPS/TVQ", paie: "Paie", acomptes: "Acomptes", req: "REQ", livres: "Livres", autre: "Autre" };

export function StatusCards({ counts, onPick, active }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="compliance-status-cards">
      {Object.entries(STATUS).map(([k, s]) => (
        <button key={k} type="button" onClick={() => onPick(k)} className={`border rounded-xl p-3 text-left transition ${s.card} ${active === k ? "ring-2 ring-offset-1 ring-slate-400" : ""}`} data-testid={`compliance-card-${k}`}>
          <div className="text-[11px] uppercase tracking-wide opacity-80">{s.label}</div>
          <div className="text-2xl font-bold tabular-nums" data-testid={`compliance-count-${k}`}>{counts?.[k] ?? 0}</div>
        </button>
      ))}
    </div>
  );
}

export function ComplianceTable({ rows, showMaster, onMark, loading, onOpenMaster }) {
  if (loading) return <div className="text-sm text-slate-500 p-6 text-center">Chargement…</div>;
  if (!rows.length) return <Empty text="Aucune échéance pour ces critères." testId="compliance-empty" />;
  return (
    <div className="bg-white border rounded-xl overflow-x-auto">
      <table className="w-full text-xs sm:text-sm" data-testid="compliance-table">
        <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">Échéance</th>
            {showMaster && <th className="text-left px-3 py-2">Dossier</th>}
            <th className="text-left px-3 py-2">Obligation</th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Catégorie</th>
            <th className="text-right px-3 py-2 hidden md:table-cell">Montant</th>
            <th className="text-left px-3 py-2">Statut</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const s = STATUS[r.status];
            return (
              <tr key={`${r.master_id || ""}:${r.key}`} className={`border-t ${r.status === "overdue" ? "bg-rose-50/40" : ""}`} data-testid={`compliance-row-${r.key}`}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{r.due}</td>
                {showMaster && <td className="px-3 py-2"><button type="button" className="text-left text-indigo-700 hover:underline" onClick={() => onOpenMaster(r.master_id)} data-testid={`compliance-open-master-${r.master_id}`}>{r.client_number ? `${r.client_number} · ` : ""}{r.master_name}</button></td>}
                <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.label}</div>{r.detail && <div className="text-[11px] text-slate-500">{r.detail}</div>}{r.note && <div className="text-[11px] text-emerald-700">Note : {r.note}</div>}</td>
                <td className="px-3 py-2 hidden md:table-cell text-slate-600">{CAT[r.category] || r.category}</td>
                <td className="px-3 py-2 hidden md:table-cell text-right font-mono">{r.amount != null ? money(r.amount) : ""}</td>
                <td className="px-3 py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={`compliance-status-${r.key}`}>{s.label}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "done"
                    ? <button type="button" className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" onClick={() => onMark(r, false)} data-testid={`compliance-undo-${r.key}`}><RotateCcw className="w-3 h-3" /> Rouvrir</button>
                    : <button type="button" className="text-[11px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 font-semibold" onClick={() => onMark(r, true)} data-testid={`compliance-done-${r.key}`}><CheckCircle2 className="w-3.5 h-3.5" /> Fait</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
